import { logger } from '@/shared/helpers/logger';
import { notifyNewVersionAvailable } from '@/shared/utils/newVersionNotifier';
import { noop } from '@/shared/utils/noop';
import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

const RETRY_DELAYS_MS: readonly number[] = [500, 1000];
const RELOAD_FLAG_KEY = 'chunk-reload-attempted';
const WINDOW_NAME_RELOAD_FLAG_PREFIX = `${RELOAD_FLAG_KEY}:`;
const RELOAD_FLAG_TTL_MS = 60_000;

type Module<T extends ComponentType<unknown>> = { default: T };
type Factory<T extends ComponentType<unknown>> = () => Promise<Module<T>>;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message ?? '';
  return /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i.test(
    msg,
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getReloadAttemptTs(): number | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { ts?: number };
      if (typeof parsed.ts === 'number') return parsed.ts;
    }
  } catch {
    noop();
  }
  try {
    const raw = globalThis.window.name;
    if (!raw.startsWith(WINDOW_NAME_RELOAD_FLAG_PREFIX)) return null;
    const ts = Number(raw.slice(WINDOW_NAME_RELOAD_FLAG_PREFIX.length));
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function hasRecentReloadAttempt(): boolean {
  const ts = getReloadAttemptTs();
  return ts !== null && Date.now() - ts < RELOAD_FLAG_TTL_MS;
}

function markReloadAttempt(): boolean {
  const ts = Date.now();
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, JSON.stringify({ ts }));
    return true;
  } catch {
    noop();
  }
  try {
    globalThis.window.name = `${WINDOW_NAME_RELOAD_FLAG_PREFIX}${ts}`;
    return true;
  } catch {
    return false;
  }
}

export function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    noop();
  }
  try {
    if (globalThis.window.name.startsWith(WINDOW_NAME_RELOAD_FLAG_PREFIX)) {
      globalThis.window.name = '';
    }
  } catch {
    noop();
  }
}

export async function retryImport<T extends ComponentType<unknown>>(
  factory: Factory<T>,
): Promise<Module<T>> {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const module = await factory();
      clearReloadFlag();
      return module;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      if (attempt < RETRY_DELAYS_MS.length) {
        logger.warn('lazyWithRetry: chunk load failed, retrying', {
          log_source: 'lazyWithRetry',
          attempt: attempt + 1,
          error,
        });
        await sleep(RETRY_DELAYS_MS[attempt] ?? 0);
        continue;
      }
      if (notifyNewVersionAvailable()) {
        logger.error('lazyWithRetry: retries exhausted, prompting new-version reload', {
          log_source: 'lazyWithRetry',
          error,
        });
        return new Promise<never>(noop);
      }
      if (!hasRecentReloadAttempt()) {
        if (!markReloadAttempt()) {
          logger.error(
            'lazyWithRetry: retries exhausted but reload guard could not be persisted, surfacing error',
            { log_source: 'lazyWithRetry', error },
          );
          throw error;
        }
        logger.error('lazyWithRetry: retries exhausted, reloading page', {
          log_source: 'lazyWithRetry',
          error,
        });
        globalThis.location.reload();
        return new Promise<never>(noop);
      }
      logger.error('lazyWithRetry: retries exhausted after prior reload, surfacing error', {
        log_source: 'lazyWithRetry',
        error,
      });
      throw error;
    }
  }
  throw new Error('lazyWithRetry: unreachable');
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: Factory<T>,
): LazyExoticComponent<T> {
  return lazy(() => retryImport(factory));
}
